/**
 * The platform's own identity: name, logo, colours, and who to credit.
 *
 * One query, shared by the shell, the login page, the footer and the branding
 * screen's preview. It is unauthenticated because the login page needs it
 * before anybody has signed in.
 *
 * `staleTime: Infinity` on purpose. Branding changes when somebody edits it,
 * which is a mutation that invalidates this key — polling for it would be one
 * request per page per minute for a value that changes a few times in the life
 * of an install.
 *
 * Every consumer gets a usable answer immediately, from `FALLBACK`, so nothing
 * renders a blank where a product name should be while the request is in
 * flight. A shell that says nothing and then says "Glee AI" a moment later
 * looks broken in a way that a shell which was right from the start does not.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { RAMP_STEPS, accentRamp, parseHex, rgbTriplet } from '@/lib/accentRamp';

export interface Branding {
  product_name: string;
  logo_url: string | null;
  accent: string;
  accent_to: string;
  tagline: string | null;
  company_name: string | null;
  company_url: string | null;
}

export const FALLBACK: Branding = {
  product_name: 'Glee AI',
  logo_url: null,
  accent: '#2563eb',
  accent_to: '#7c3aed',
  tagline: 'Voice AI Agents for a Smarter Tomorrow',
  company_name: 'Glee Telecom Global LLC',
  company_url: 'https://gleetelecom.com',
};

/** The version this bundle was built from. Baked in by Vite. */
export const UI_VERSION =
  typeof __UI_VERSION__ === 'string' ? __UI_VERSION__ : 'dev';

export function useBranding(): Branding {
  const { data } = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<Branding>('/branding'),
    staleTime: Infinity,
    retry: 1,
  });
  return { ...FALLBACK, ...(data ?? {}) };
}

/**
 * Push the accent into the stylesheet.
 *
 * Two custom properties rather than one, because the mark runs blue into
 * violet and a single flat accent beside it looks like another product's
 * chrome around somebody else's logo. Anything wanting the pair together uses
 * `--brand-gradient`.
 *
 * Set on the root element rather than compiled into the bundle: the colour is
 * a database value an operator can change, and a rebuild is not something a
 * colour picker should require.
 */
export function applyAccent(accent: string, accentTo: string): void {
  const root = document.documentElement;
  root.style.setProperty('--accent', accent);
  root.style.setProperty('--accent-to', accentTo);
  root.style.setProperty(
    '--brand-gradient',
    `linear-gradient(135deg, ${accent} 0%, ${accentTo} 100%)`,
  );

  // The rest of the brand ramp follows the same colour, so links, tab
  // underlines, focus rings and selected rows match the primary button rather
  // than staying the compiled-in blue. See `accentRamp` for why it is built in
  // OKLCH. A colour the browser cannot parse leaves the previous ramp in
  // place -- the stylesheet defaults on first load -- rather than blanking it.
  const rgb = parseHex(normaliseColour(accent) ?? '');
  if (!rgb) return;
  root.style.setProperty('--accent-rgb', rgbTriplet(rgb));
  // Text on the accent. White on a pale yellow accent is unreadable, and the
  // primary button is the one control nobody should have to squint at.
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  root.style.setProperty('--accent-fg', luminance > 0.45 ? '15 17 21' : '255 255 255');
  const ramp = accentRamp(rgb);
  for (const step of RAMP_STEPS) {
    root.style.setProperty(`--brand-${step}`, ramp[step]);
  }
}

/**
 * Any CSS colour the operator typed, as `#rrggbb` -- or null if it is not one.
 *
 * The Branding screen has a free-text box beside the picker, so "teal" or
 * "rgb(0 128 128)" are possible values. A canvas context normalises whatever
 * the browser accepts into hex, and ignores what it does not.
 */
function normaliseColour(value: string): string | null {
  if (parseHex(value)) return value.trim();
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#010203';
    ctx.fillStyle = value;
    const out = String(ctx.fillStyle);
    if (out === '#010203' && value.trim().toLowerCase() !== '#010203') return null;
    return out.startsWith('#') ? out : null;
  } catch {
    return null;
  }
}

/**
 * Who made this, and which release you are looking at.
 *
 * On every page. The version is the half that earns its place: "which build is
 * this?" is the first question of every bug report, and the answer was
 * previously only available by opening the network tab or waiting for the
 * stale-bundle notice to decide there was a mismatch.
 */
export function AppFooter({ className = '' }: { className?: string }) {
  const branding = useBranding();

  return (
    <footer
      className={`border-t border-line px-4 py-2.5 text-2xs text-ink-500 lg:px-8 dark:text-ink-400 ${className}`}
    >
      <div className="mx-auto flex max-w-[90rem] flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p>
          {branding.product_name}
          {branding.company_name && (
            <>
              {' — powered by '}
              {branding.company_url ? (
                <a
                  href={branding.company_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand-600 hover:underline dark:text-brand-400"
                >
                  {branding.company_name}
                </a>
              ) : (
                branding.company_name
              )}
            </>
          )}
        </p>
        <p className="tabular-nums">v{UI_VERSION}</p>
      </div>
    </footer>
  );
}
